import React, { useState, useEffect, useRef } from 'react';
import './PurchaseForm.css';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faDollarSign, faCheckCircle, faExclamationTriangle, faCopy, faCheck, faEnvelope, faWallet } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
// ModalPagoMovil ya no es necesario porque mostraremos los datos en línea
import baseURL from '../url';

export default function PurchaseForm({ hasEnoughNumbers = true, numerosDisponibles = 0, idRifa = null, rifa = null }) {
    const formRef = useRef(null);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        quantity: 1,
        fullName: '',
        phone: '',
        cedula: '',
        email: '',
        location: '',
        paymentMethod: '',
        reference: '',
        proofFile: null
    });
    const [acceptRandomNumbers, setAcceptRandomNumbers] = useState(true);
    const [imagePreview, setImagePreview] = useState(null);
    // Datos de copia para Pago Móvil en línea
    const [copiedItems, setCopiedItems] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [assignedNumbers, setAssignedNumbers] = useState([]);
    const [countdown, setCountdown] = useState(10);
    const [isError, setIsError] = useState(false);
    const [savedFormData, setSavedFormData] = useState(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isEditingQuantity, setIsEditingQuantity] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Efecto para limpiar el estado del navegador al cargar el componente
    useEffect(() => {
        // Limpiar cualquier estado persistente del navegador
        const clearBrowserState = () => {
            // Limpiar localStorage relacionado con el formulario
            localStorage.removeItem('purchaseFormData');
            localStorage.removeItem('purchaseQuantity');
            localStorage.removeItem('purchaseNumbers');
            
            // Limpiar sessionStorage
            sessionStorage.removeItem('purchaseFormData');
            sessionStorage.removeItem('purchaseQuantity');
            sessionStorage.removeItem('purchaseNumbers');
            
            // Limpiar el estado del formulario
            setFormData({
                quantity: 1,
                fullName: '',
                phone: '',
                cedula: '',
                email: '',
                location: '',
                paymentMethod: '',
                reference: '',
                proofFile: null
            });
            setImagePreview(null);
            setAcceptRandomNumbers(true);
            setIsSubmitting(false);
            setIsSendingEmail(false);
        };
        
        clearBrowserState();
    }, []); // Solo ejecutar al montar el componente

    // Efecto para prevenir envíos duplicados en la misma sesión
    useEffect(() => {
        const preventDuplicateSubmit = (e) => {
            if (isSubmitting) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };
        
        // Usar useRef para evitar manipulación directa del DOM
        const formElement = formRef.current;
        if (formElement) {
            formElement.addEventListener('submit', preventDuplicateSubmit, true);
        }
        
        return () => {
            if (formElement) {
                formElement.removeEventListener('submit', preventDuplicateSubmit, true);
            }
        };
    }, [isSubmitting]);

    const handleCloseSuccessModal = (openWhatsapp = false) => {
        const wasSuccess = !isError;

        if (wasSuccess && openWhatsapp) {
            openWhatsApp(assignedNumbers);
        }

        setShowSuccessModal(false);
        setIsError(false);
        setSavedFormData(null);

        if (wasSuccess) {
            window.location.reload();
        }
    };

    // Efecto para el contador regresivo (solo para mostrar, sin cerrar automáticamente)
    useEffect(() => {
        let timer;
        if (showSuccessModal && countdown > 0) {
            timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [showSuccessModal, countdown]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validar que solo se ingresen números en el campo cédula
        if (name === 'cedula') {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } 
        // Referencia: numérica para pago móvil, alfanumérica para Zelle y Binance
        else if (name === 'reference') {
            setFormData(prev => {
                const refValue = (prev.paymentMethod === 'zelle' || prev.paymentMethod === 'binance')
                    ? value.slice(0, 50)
                    : value.replace(/[^0-9]/g, '').slice(0, 12);
                return { ...prev, [name]: refValue };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                proofFile: file
            }));
            
            // Crear previsualización de la imagen
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);

            // El modal de pago móvil permanece abierto hasta que el usuario lo cierre manualmente
        }
    };

    const handleQuantityChange = (quantity) => {
        // Ensure quantity is within limits (1 to available numbers)
        const maxQuantity = Math.min(100, numerosDisponibles);
        const clampedQuantity = Math.max(1, Math.min(maxQuantity, quantity));
        setFormData(prev => ({
            ...prev,
            quantity: clampedQuantity
        }));
    };

    const handleQuantityIncrement = () => {
        const maxQuantity = Math.min(100, numerosDisponibles);
        setFormData(prev => ({
            ...prev,
            quantity: Math.min(maxQuantity, prev.quantity + 1)
        }));
    };

    const handleQuantityDecrement = () => {
        setFormData(prev => ({
            ...prev,
            quantity: Math.max(1, prev.quantity - 1)
        }));
    };

    // Función para verificar si los botones deben estar habilitados
    const areButtonsEnabled = () => {
        return numerosDisponibles >= 1 && !isSubmitting;
    };

    const handleQuantityInputChange = (e) => {
        const value = e.target.value;
        // Solo permitir números
        if (value === '' || /^\d+$/.test(value)) {
            const numValue = value === '' ? 1 : parseInt(value, 10);
            const maxQuantity = Math.min(100, numerosDisponibles);
            const clampedQuantity = Math.max(1, Math.min(maxQuantity, numValue));
            
            // Si el usuario escribió más de 100, mostrar 100 inmediatamente
            if (numValue > 100) {
                setFormData(prev => ({
                    ...prev,
                    quantity: 100
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    quantity: clampedQuantity
                }));
            }
        }
    };

    const handleQuantityInputFocus = () => {
        setIsEditingQuantity(true);
    };

    const handleQuantityInputBlur = () => {
        setIsEditingQuantity(false);
        // Asegurar que el valor esté dentro de los límites al perder el foco
        const maxQuantity = Math.min(100, numerosDisponibles);
        const clampedQuantity = Math.max(1, Math.min(maxQuantity, formData.quantity));
        
        // Si el valor es mayor a 100, establecer a 100
        if (formData.quantity > 100) {
            setFormData(prev => ({
                ...prev,
                quantity: 100
            }));
        } else if (clampedQuantity !== formData.quantity) {
            setFormData(prev => ({
                ...prev,
                quantity: clampedQuantity
            }));
        }
    };

    const handleQuantityInputKeyDown = (e) => {
        // Permitir teclas de navegación y control
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)) {
            return;
        }
        // Solo permitir números
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    // Copiar al portapapeles (Pago Móvil)
    const handleCopy = async (text, itemKey) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedItems(prev => ({ ...prev, [itemKey]: true }));
            setTimeout(() => {
                setCopiedItems(prev => ({ ...prev, [itemKey]: false }));
            }, 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedItems(prev => ({ ...prev, [itemKey]: true }));
            setTimeout(() => {
                setCopiedItems(prev => ({ ...prev, [itemKey]: false }));
            }, 2000);
        }
    };

    const getPaymentMethodLabel = (method) => {
        switch (method) {
            case 'mobile': return 'Pago móvil';
            case 'zelle': return 'Zelle';
            case 'binance': return 'Binance';
            default: return method;
        }
    };

    const getPurchasePriceInfo = (paymentMethod) => {
        const quantity = formData.quantity || 1;

        switch (paymentMethod) {
            case 'zelle':
                return {
                    unitPrice: rifa?.precio_zelle || 0,
                    currency: 'USD',
                    total: (Number(rifa?.precio_zelle) || 0) * quantity
                };
            case 'binance':
                return {
                    unitPrice: rifa?.precio_binance || 0,
                    currency: 'USDT',
                    total: (Number(rifa?.precio_binance) || 0) * quantity
                };
            case 'mobile':
            default:
                return {
                    unitPrice: rifa?.precio || 0,
                    currency: rifa?.moneda || '',
                    total: (Number(rifa?.precio) || 0) * quantity
                };
        }
    };

    const handlePaymentMethodChange = (method) => {
        setFormData(prev => ({
            ...prev,
            paymentMethod: method,
            reference: ''
        }));
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            proofFile: null
        }));
        setImagePreview(null);
        // Limpiar el input file usando ref
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const isFormValid = () => {
        const binanceValid = formData.paymentMethod !== 'binance' ||
            (formData.reference.trim() !== '' && formData.proofFile !== null);

        return hasEnoughNumbers &&
               formData.fullName.trim() !== '' &&
               formData.phone.trim() !== '' &&
               formData.cedula.trim() !== '' &&
               formData.email.trim() !== '' &&
               formData.location.trim() !== '' &&
               formData.reference.trim() !== '' &&
               binanceValid &&
               acceptRandomNumbers &&
               !isSubmitting;
    };

    const generateWhatsAppMessage = (numerosAsignados) => {
        const rifaNombre = rifa?.nombre || 'la rifa';
        const numerosTexto = numerosAsignados.join(', ');
        const dataToUse = savedFormData || formData;
        const priceInfo = getPurchasePriceInfo(dataToUse.paymentMethod);
        
        const mensaje = `Hola Suerte Solidaria! Confirmo mi participacion en la rifa ${rifaNombre} (${priceInfo.currency} ${priceInfo.unitPrice}). Datos: ${dataToUse.fullName}, ${dataToUse.phone}, ${dataToUse.email}, ${dataToUse.location}. Metodo de pago: ${getPaymentMethodLabel(dataToUse.paymentMethod)}${dataToUse.reference ? `, Referencia: ${dataToUse.reference}` : ''}. Acepto terminos y condiciones.`;

        return mensaje;
    };

    const enviarEmailConfirmacion = async (numerosAsignados, datosFormulario) => {
        setIsSendingEmail(true);
        try {
            const formDataEmail = new FormData();
            formDataEmail.append('idRifa', idRifa || '1');
            formDataEmail.append('numeros', JSON.stringify(numerosAsignados));
            formDataEmail.append('fullName', datosFormulario.fullName);
            formDataEmail.append('phone', datosFormulario.phone);
            formDataEmail.append('email', datosFormulario.email);
            formDataEmail.append('location', datosFormulario.location);
            formDataEmail.append('paymentMethod', datosFormulario.paymentMethod);
            formDataEmail.append('reference', datosFormulario.reference);
            
            const response = await fetch(`${baseURL}/enviarEmail.php`, {
                method: 'POST',
                body: formDataEmail,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                return { success: true, message: result.message };
            } else {
                return { success: false, error: result.error };
            }
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            setIsSendingEmail(false);
        }
    };

    const openWhatsApp = (numerosAsignados) => {
        const mensaje = generateWhatsAppMessage(numerosAsignados);
        const whatsappUrl = `https://wa.me/584221437681?text=${encodeURIComponent(mensaje)}`;

        try {
            // Intentar abrir en nueva pestaña
            const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            
            // Verificar si se abrió correctamente
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                // Si no se pudo abrir, intentar con location.href
                window.location.href = whatsappUrl;
            } else {
            }
        } catch (error) {
            // Como último recurso, usar location.href
            window.location.href = whatsappUrl;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevenir múltiples envíos
        if (isSubmitting || !isFormValid()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
                // Crear FormData para enviar archivos
                const formDataToSend = new FormData();
                formDataToSend.append('idRifa', idRifa || '1'); // Usar el idRifa pasado como prop
                formDataToSend.append('quantity', formData.quantity);
                formDataToSend.append('fullName', formData.fullName);
                formDataToSend.append('phone', formData.phone);
                formDataToSend.append('cedula', formData.cedula);
                formDataToSend.append('email', formData.email);
                formDataToSend.append('location', formData.location);
                // Si no se selecciona método de pago, usar 'no especificado' como valor por defecto
                formDataToSend.append('paymentMethod', formData.paymentMethod || 'no especificado');
                formDataToSend.append('reference', formData.reference);
                
                // Agregar la imagen si existe
                if (formData.proofFile) {
                    formDataToSend.append('imagen_comprobante', formData.proofFile);
                }
                
                // Usar directamente el servidor PHP integrado
                const response = await fetch(`${baseURL}/compraPost.php`, {
                    method: 'POST',
                    body: formDataToSend,
                    mode: 'cors'
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.error) {
                    // Mostrar error en el modal con mensaje específico
                    setAssignedNumbers([]);
                    setIsError(true);
                    setShowSuccessModal(true);
                    setCountdown(5);
                    
                    // Si es error de compra reciente, mostrar mensaje específico
                    if (result.error.includes('compra pendiente muy reciente')) {
                        console.log('Compra muy reciente detectada. El usuario debe esperar 2 minutos.');
                    }
                } else {
                    // PASO 1: Guardar los datos del formulario antes de limpiarlos
                    setSavedFormData({ ...formData });
                    
                    // PASO 2: Guardar los números asignados
                    setAssignedNumbers(result.numeros);
                    
                    // PASO 3: Enviar email y esperar a que se complete
                    const emailResult = await enviarEmailConfirmacion(result.numeros, formData);
                    
                    // PASO 4: Solo después de procesar el email, mostrar el modal
                    setShowSuccessModal(true);
                    setCountdown(5);
                    
                    // Limpiar el formulario completamente
                    const clearForm = () => {
                        setFormData({
                            quantity: 1,
                            fullName: '',
                            phone: '',
                            cedula: '',
                            email: '',
                            location: '',
                            paymentMethod: '',
                            reference: '',
                            proofFile: null
                        });
                        setImagePreview(null);
                        setAcceptRandomNumbers(true);
                        
                        // Limpiar el input de archivo usando ref
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                        
                        // Limpiar cualquier estado persistente
                        localStorage.removeItem('purchaseFormData');
                        localStorage.removeItem('purchaseQuantity');
                        sessionStorage.removeItem('purchaseFormData');
                        sessionStorage.removeItem('purchaseQuantity');
                    };
                    
                    clearForm();
                }
            } catch (error) {
                console.error('Error en handleSubmit:', error);
                // Mostrar error en el modal
                setAssignedNumbers([]);
                setIsError(true);
                setShowSuccessModal(true);
                setCountdown(5);
            } finally {
                // Siempre resetear el estado de envío
                setIsSubmitting(false);
            }
    };

    const purchasePriceInfo = getPurchasePriceInfo(formData.paymentMethod);

    return (
        <div className="purchase-form-container">
            <form ref={formRef} onSubmit={handleSubmit} className="purchase-form">
                {/* Paso 1: Cantidad de Tickets */}
                <div className="form-step">
                    <h3>Paso 1: Cantidad de Tickets</h3>
                    {/* <p className="step-subtitle">
                        Mínimo 1 Ticket
                    </p> */}
                    <div className="quantity-selector">
                        <div className="quantity-display">
                            <button 
                                type="button" 
                                className={`quantity-btn ${!areButtonsEnabled() ? 'disabled' : ''}`}
                                onClick={handleQuantityDecrement}
                                disabled={formData.quantity <= 1 || !areButtonsEnabled()}
                            >
                                <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <input
                                type="text"
                                value={formData.quantity}
                                onChange={handleQuantityInputChange}
                                onFocus={handleQuantityInputFocus}
                                onBlur={handleQuantityInputBlur}
                                onKeyDown={handleQuantityInputKeyDown}
                                className={`quantity-input ${!areButtonsEnabled() ? 'disabled' : ''}`}
                                disabled={!areButtonsEnabled()}
                                maxLength={3}
                            />
                            <button 
                                type="button" 
                                className={`quantity-btn ${!areButtonsEnabled() ? 'disabled' : ''}`}
                                onClick={handleQuantityIncrement}
                                disabled={formData.quantity >= Math.min(100, numerosDisponibles) || !areButtonsEnabled()}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        </div>
                        <div className="quick-quantity-buttons">
                            <button 
                                type="button" 
                                className={`quick-quantity-btn ${!areButtonsEnabled() || numerosDisponibles < 5 ? 'disabled' : ''}`}
                                onClick={() => handleQuantityChange(5)}
                                disabled={!areButtonsEnabled() || numerosDisponibles < 5}
                            >
                                5
                            </button>
                            <button 
                                type="button" 
                                className={`quick-quantity-btn ${!areButtonsEnabled() || numerosDisponibles < 10 ? 'disabled' : ''}`}
                                onClick={() => handleQuantityChange(10)}
                                disabled={!areButtonsEnabled() || numerosDisponibles < 10}
                            >
                                10
                            </button>
                            <button 
                                type="button" 
                                className={`quick-quantity-btn ${!areButtonsEnabled() || numerosDisponibles < 20 ? 'disabled' : ''}`}
                                onClick={() => handleQuantityChange(20)}
                                disabled={!areButtonsEnabled() || numerosDisponibles < 20}
                            >
                                20
                            </button>
                            <button 
                                type="button" 
                                className={`quick-quantity-btn ${!areButtonsEnabled() || numerosDisponibles < 50 ? 'disabled' : ''}`}
                                onClick={() => handleQuantityChange(50)}
                                disabled={!areButtonsEnabled() || numerosDisponibles < 50}
                            >
                                50
                            </button>
                            <button 
                                type="button" 
                                className={`quick-quantity-btn ${!areButtonsEnabled() || numerosDisponibles < 100 ? 'disabled' : ''}`}
                                onClick={() => handleQuantityChange(100)}
                                disabled={!areButtonsEnabled() || numerosDisponibles < 100}
                            >
                                100
                            </button>
                        </div>
                    </div>
                </div>

                {/* Paso 2: Datos del participante */}
                <div className="form-step">
                    <h3>Paso 2: Datos del participante</h3>
                    <div className="form-fields">
                        <input
                            type="text"
                            name="fullName"
                            placeholder="Nombre Completo"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                        />
                        <input
                            type="tel"
                            name="phone"
                            placeholder="Teléfono"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                        />
                        <input
                            type="text"
                            name="cedula"
                            placeholder="Cédula"
                            value={formData.cedula}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                            pattern="[0-9]*"
                            inputMode="numeric"
                        />
                        <input
                            type="email"
                            name="email"
                            placeholder="E-mail"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                        />
                        <input
                            type="text"
                            name="location"
                            placeholder="¿Dónde vives?"
                            value={formData.location}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                        />
                    </div>
                </div>

                {/* Paso 3: Método de pago */}
                <div className="form-step">
                    <h3>Paso 3: Método de pago</h3>
                    <p className="step-subtitle">
                        Selecciona un método de pago.
                    </p>
                    <div className="payment-methods">
                        <button
                            type="button"
                            className={`payment-method-btn ${formData.paymentMethod === 'mobile' ? 'selected' : ''} ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            onClick={() => handlePaymentMethodChange('mobile')}
                            disabled={!hasEnoughNumbers}
                        >
                            <FontAwesomeIcon icon={faDollarSign} className="payment-icon" />
                            Pago móvil
                        </button>
                        <button
                            type="button"
                            className={`payment-method-btn ${formData.paymentMethod === 'zelle' ? 'selected' : ''} ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            onClick={() => handlePaymentMethodChange('zelle')}
                            disabled={!hasEnoughNumbers}
                        >
                            <FontAwesomeIcon icon={faEnvelope} className="payment-icon" />
                            Zelle
                        </button>
                        <button
                            type="button"
                            className={`payment-method-btn ${formData.paymentMethod === 'binance' ? 'selected' : ''} ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            onClick={() => handlePaymentMethodChange('binance')}
                            disabled={!hasEnoughNumbers}
                        >
                            <FontAwesomeIcon icon={faWallet} className="payment-icon" />
                            Binance
                        </button>
                    </div>

                    {(formData.paymentMethod === 'mobile' || formData.paymentMethod === 'zelle' || formData.paymentMethod === 'binance') && (
                        <div className="mt-3 p-3 bg-light rounded payment-details-box">
                            <h5 className="mb-3 text-center">Resumen de Compra</h5>
                            <div className="row">
                                <div className="col-6">
                                    <small className="text-muted">Precio por ticket:</small>
                                    <div className="fw-bold">{purchasePriceInfo.currency} {purchasePriceInfo.unitPrice}</div>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">Cantidad de tickets:</small>
                                    <div className="fw-bold">{formData.quantity}</div>
                                </div>
                            </div>
                            <hr />
                            <div className="text-center">
                                <small className="text-muted">Total a pagar:</small>
                                <div className="h4 text-primary fw-bold">{purchasePriceInfo.currency} {purchasePriceInfo.total}</div>
                            </div>

                            {formData.paymentMethod === 'mobile' && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Datos para realizar el pago móvil:</h6>

                                    <div className="mb-3">
                                        <label className="form-label text-muted">Cédula:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="26.392.627" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('26.392.627', 'cedula')}
                                                title="Copiar cédula"
                                                style={{ backgroundColor: '#28a745', borderColor: '#28a745', color: 'white' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.cedula ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label text-muted">Teléfono:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="04144847438" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('04144847438', 'telefono')}
                                                title="Copiar teléfono"
                                                style={{ backgroundColor: '#28a745', borderColor: '#28a745', color: 'white' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.telefono ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="form-label text-muted">Banco:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="Banco Provincial" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('Banco Provincial', 'banco')}
                                                title="Copiar banco"
                                                style={{ backgroundColor: '#28a745', borderColor: '#28a745', color: 'white' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.banco ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {formData.paymentMethod === 'zelle' && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Datos para realizar el pago con Zelle:</h6>
                                    <p className="text-muted small mb-3">
                                        Envía el pago desde tu app Zelle al correo indicado y adjunta el comprobante en el paso 4.
                                    </p>

                                    <div className="mb-3">
                                        <label className="form-label text-muted">Nombre:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="Yugreisy Torrealba" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('Yugreisy Torrealba', 'zelleNombre')}
                                                title="Copiar nombre"
                                                style={{ backgroundColor: '#6B21A8', borderColor: '#6B21A8', color: 'white' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.zelleNombre ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="form-label text-muted">Email Zelle:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="Yugreisybetsabe@gmail.com" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('Yugreisybetsabe@gmail.com', 'zelleEmail')}
                                                title="Copiar email"
                                                style={{ backgroundColor: '#6B21A8', borderColor: '#6B21A8', color: 'white' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.zelleEmail ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {formData.paymentMethod === 'binance' && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Datos para realizar el pago con Binance:</h6>
                                    <p className="text-muted small mb-3">
                                        Envía el pago desde tu app Binance al email o ID indicado y adjunta el comprobante en el paso 4.
                                    </p>

                                    <div className="mb-3">
                                        <label className="form-label text-muted">Email Binance:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="jesusamr83@gmail.com" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('jesusamr83@gmail.com', 'binanceEmail')}
                                                title="Copiar email"
                                                style={{ backgroundColor: '#F0B90B', borderColor: '#F0B90B', color: '#1E2329' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.binanceEmail ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="form-label text-muted">ID Binance:</label>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value="493703776" readOnly />
                                            <Button
                                                variant="primary"
                                                onClick={() => handleCopy('493703776', 'binanceId')}
                                                title="Copiar ID"
                                                style={{ backgroundColor: '#F0B90B', borderColor: '#F0B90B', color: '#1E2329' }}
                                            >
                                                <FontAwesomeIcon icon={copiedItems.binanceId ? faCheck : faCopy} />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <small className="text-muted d-block mt-3">
                                {formData.paymentMethod === 'binance'
                                    ? 'El comprobante y tu email/ID de Binance son obligatorios en el paso 4.'
                                    : 'El número de referencia es obligatorio. Adjunta el comprobante de pago en el paso 4.'}
                            </small>
                        </div>
                    )}
                </div>

                {/* Paso 4: Subir comprobante */}
                <div className="form-step">
                    <h3>
                        Paso 4: Comprobante de pago{' '}
                    </h3>
                    <p className="step-subtitle">
                        {formData.paymentMethod === 'binance'
                            ? 'Adjunta la captura del comprobante de pago por Binance e indica tu email o ID de usuario de Binance. Ambos campos son obligatorios.'
                            : formData.paymentMethod === 'zelle'
                            ? 'Adjunta la captura del comprobante de tu pago Zelle. El número de referencia es obligatorio.'
                            : 'Adjunta la foto del pago. El número de referencia es obligatorio.'}
                        {(formData.paymentMethod === 'binance'
                            ? formData.reference.trim() !== '' && formData.proofFile
                            : formData.reference.trim() !== '') && (
                            <span className="validation-success"> ✅ Listo para enviar</span>
                        )}
                    </p>
                    <div className="upload-section">
                        {!imagePreview ? (
                            <div className="file-upload-area">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    id="proof-upload"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="file-input"
                                    disabled={!hasEnoughNumbers}
                                />
                                <label htmlFor="proof-upload" className={`file-upload-label ${!hasEnoughNumbers ? 'disabled' : ''}`}>
                                    <div className="upload-icon">📄</div>
                                    <p>Haz click para subir el comprobante</p>
                                </label>
                            </div>
                        ) : (
                            <div className="image-preview-container">
                                <div className="image-preview">
                                    <img src={imagePreview} alt="Comprobante" className="preview-image" />
                                    <button 
                                        type="button" 
                                        className="remove-image-btn"
                                        onClick={removeImage}
                                        title="Eliminar imagen"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="image-info">
                                    <p className="image-name">{formData.proofFile?.name}</p>
                                    <button 
                                        type="button" 
                                        className="change-image-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Cambiar imagen
                                    </button>
                                </div>
                            </div>
                        )}
                        <input
                            type="text"
                            name="reference"
                            placeholder={
                                formData.paymentMethod === 'binance'
                                    ? 'Email o ID de usuario de Binance'
                                    : formData.paymentMethod === 'zelle'
                                    ? 'Referencia o número de confirmación Zelle'
                                    : 'Referencia manual (solo números)'
                            }
                            value={formData.reference}
                            onChange={handleInputChange}
                            className={`form-input ${!hasEnoughNumbers ? 'disabled' : ''}`}
                            disabled={!hasEnoughNumbers}
                            pattern={formData.paymentMethod === 'zelle' || formData.paymentMethod === 'binance' ? undefined : '[0-9]*'}
                            inputMode={formData.paymentMethod === 'zelle' || formData.paymentMethod === 'binance' ? 'text' : 'numeric'}
                            maxLength={formData.paymentMethod === 'zelle' || formData.paymentMethod === 'binance' ? 50 : 12}
                        />
                    </div>
                </div>

                {/* Checkbox and Submit */}
                <div className="form-footer">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={acceptRandomNumbers}
                            onChange={(e) => setAcceptRandomNumbers(e.target.checked)}
                            className="checkbox-input"
                            disabled={!hasEnoughNumbers}
                        />
                        <span className="checkbox-text">
                            Acepto que mis números de participación sean aleatorios.
                        </span>
                    </label>
                    <button 
                        type="submit" 
                        className={`submit-btn ${!isFormValid() || isSendingEmail || isSubmitting ? 'disabled' : ''}`}
                        disabled={!isFormValid() || isSendingEmail || isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="loading-spinner">⏳</span>
                                Procesando Compra...
                            </>
                        ) : isSendingEmail ? (
                            <>
                                <span className="loading-spinner">⏳</span>
                                Enviando Email...
                            </>
                        ) : (
                            'Enviar'
                        )}
                    </button>
                </div>
            </form>
            
            {/* Datos de Pago Móvil ahora se muestran en línea debajo del botón */}
            
            {/* Modal de participación exitosa con Bootstrap */}
            <Modal 
                show={showSuccessModal} 
                onHide={() => handleCloseSuccessModal(false)}
                size="md"
                centered
                backdrop="static"
                keyboard={true}
            >
                <Modal.Header 
                    closeButton 
                    className={isError ? "bg-danger text-white" : "bg-success text-white"}
                >
                    <Modal.Title className="d-flex align-items-center">
                        <FontAwesomeIcon 
                            icon={isError ? faExclamationTriangle : faCheckCircle} 
                            className="me-2" 
                        />
                        {isError ? "Error en la Participación" : "¡Procesando Pago!"}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="text-center">
                    {!isError ? (
                        <>
                            {/* <div className="mb-3">
                                <FontAwesomeIcon 
                                    icon={faCheckCircle} 
                                    className="text-success" 
                                    style={{ fontSize: '3rem' }}
                                />
                            </div> */}
                            <p className="mb-3 text-start">
                            1. Presiona “Entendido” para ir a WhatsApp con un mensaje prellenado. <br/>
                            2. Solo debes hacer clic en “Enviar” para confirmar tu compra. <br />
                            3. Tus tickets serán enviados a tu correo en un plazo máximo de 24 horas.
                            </p>
                            
                            {/* Sección de números asignados (comentada por ahora) */}
                            {/* {assignedNumbers.length > 0 && (
                                <div className="mb-3">
                                    <h5>Tus números de la suerte:</h5>
                                    <div className="d-flex flex-wrap justify-content-center gap-2">
                                        {assignedNumbers.map((numero, index) => (
                                            <span key={index} className="badge bg-primary fs-6">
                                                {numero}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )} */}
                            
                            {/* Sección de WhatsApp (comentada por ahora) */}
                            {/* <div className="mb-3">
                                <div className="mb-2">
                                    <FontAwesomeIcon icon={faWhatsapp} className="text-success me-2" />
                                    <span>Redirigiendo a WhatsApp...</span>
                                </div>
                                <div className="h3 text-primary">{countdown}</div>
                                <Button 
                                    variant="success"
                                    onClick={() => openWhatsApp(assignedNumbers)}
                                    className="mt-2"
                                >
                                    <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                                    Abrir WhatsApp Ahora
                                </Button>
                            </div> */}
                        </>
                    ) : (
                        <>
                            <div className="mb-3">
                                <FontAwesomeIcon 
                                    icon={faExclamationTriangle} 
                                    className="text-danger" 
                                    style={{ fontSize: '3rem' }}
                                />
                            </div>
                            <p className="mb-3">
                                Hubo un problema al procesar tu participación. Por favor, intenta nuevamente.
                            </p>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="bg-light">
                    <Button 
                        variant={isError ? "danger" : "success"}
                        onClick={() => handleCloseSuccessModal(!isError)}
                        className="w-100"
                    >
                        {isError ? "Cerrar" : "Entendido"}
                    </Button>
                </Modal.Footer>
            </Modal>
            
        </div>
    );
}
